import { Link } from 'react-router-dom';
import { ShoppingCart, Menu, User } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';

const Navbar = () => {
  const { cartCount } = useCart();
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b-4 border-black relative overflow-hidden">
      {/* Decorative Elements */}
      {/* Stars */}
      <div className="absolute top-2 left-8 w-4 h-4 text-brand_yellow opacity-60">
        <div className="w-full h-full" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }}>★</div>
      </div>
      <div className="absolute top-3 right-32 w-3 h-3 text-brand_blue opacity-50 animate-pulse">
        <div className="w-full h-full" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }}>★</div>
      </div>
      <div className="absolute bottom-2 left-1/4 w-3 h-3 text-brand_red opacity-40">
        <div className="w-full h-full" style={{ clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' }}>★</div>
      </div>
      
      {/* Fruit Elements */}
      <div className="absolute top-1 left-24 text-2xl opacity-30 animate-bounce" style={{ animationDuration: '3s' }}>🍋</div>
      <div className="absolute bottom-1 right-48 text-xl opacity-25 animate-pulse" style={{ animationDuration: '4s' }}>🍓</div>
      <div className="absolute top-2 right-1/3 text-xl opacity-20">🥭</div>
      
      {/* Water Waves */}
      <div className="absolute bottom-0 left-0 right-0 h-1 opacity-30">
        <svg className="w-full h-full" viewBox="0 0 1200 20" preserveAspectRatio="none">
          <path d="M0,10 Q150,0 300,10 T600,10 T900,10 T1200,10 L1200,20 L0,20 Z" fill="#0044CC" opacity="0.3">
            <animate attributeName="d" dur="3s" repeatCount="indefinite"
              values="M0,10 Q150,0 300,10 T600,10 T900,10 T1200,10 L1200,20 L0,20 Z;
                      M0,10 Q150,20 300,10 T600,10 T900,10 T1200,10 L1200,20 L0,20 Z;
                      M0,10 Q150,0 300,10 T600,10 T900,10 T1200,10 L1200,20 L0,20 Z"/>
          </path>
        </svg>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <h1 className="font-chewy text-2xl sm:text-3xl">
              <span className="text-brand_blue drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]">UNCLE</span>{' '}
              <span className="text-brand_red drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]">DAVE'S</span>{' '}
              <span className="text-brand_yellow drop-shadow-[2px_2px_0px_rgba(0,0,0,1)]">LEMONADE</span>
            </h1>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="font-nunito font-bold text-black hover:text-brand_blue"
              data-testid="navbar-home-link"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="font-nunito font-bold text-black hover:text-brand_blue"
              data-testid="navbar-about-link"
            >
              Our Story
            </Link>
            <Link
              to="/products"
              className="font-nunito font-bold text-black hover:text-brand_blue"
              data-testid="navbar-products-link"
            >
              Products
            </Link>
            <Link
              to="/popup-program"
              className="font-nunito font-bold text-black hover:text-brand_blue"
              data-testid="navbar-popup-link"
            >
              Pop-Up Program
            </Link>
            <Link
              to="/track-order"
              className="font-nunito font-bold text-black hover:text-brand_blue"
              data-testid="navbar-track-link"
            >
              Track Order
            </Link>
            {isAuthenticated ? (
              <Link
                to="/account"
                className="bg-brand_yellow text-black px-6 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
                data-testid="navbar-account-button"
              >
                <User className="inline-block w-5 h-5 mr-1" strokeWidth={3} />
                {user?.name?.split(' ')[0] || 'Account'}
              </Link>
            ) : (
              <Link
                to="/login"
                className="bg-brand_yellow text-black px-6 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
                data-testid="navbar-login-button"
              >
                <User className="inline-block w-5 h-5 mr-1" strokeWidth={3} />
                Login
              </Link>
            )}
            <Link
              to="/cart"
              className="relative bg-brand_blue text-white px-6 py-2 rounded-full font-nunito font-bold border-2 border-black shadow-hard hover:shadow-hard-hover hover:-translate-y-0.5 transition-all"
              data-testid="navbar-cart-button"
            >
              <ShoppingCart className="inline-block w-5 h-5" strokeWidth={3} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-brand_red text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold border-2 border-black">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" strokeWidth={3} />
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-3">
            <Link
              to="/"
              className="block font-nunito font-bold text-black hover:text-brand_blue"
              onClick={() => setMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/about"
              className="block font-nunito font-bold text-black hover:text-brand_blue"
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Story
            </Link>
            <Link
              to="/products"
              className="block font-nunito font-bold text-black hover:text-brand_blue"
              onClick={() => setMobileMenuOpen(false)}
            >
              Products
            </Link>
            <Link
              to="/popup-program"
              className="block font-nunito font-bold text-black hover:text-brand_blue"
              onClick={() => setMobileMenuOpen(false)}
            >
              Pop-Up Program
            </Link>
            <Link
              to="/track-order"
              className="block font-nunito font-bold text-black hover:text-brand_blue"
              onClick={() => setMobileMenuOpen(false)}
            >
              Track Order
            </Link>
            {isAuthenticated ? (
              <Link
                to="/account"
                className="block w-full text-center bg-brand_yellow text-black px-6 py-3 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
                onClick={() => setMobileMenuOpen(false)}
              >
                My Account
              </Link>
            ) : (
              <Link
                to="/login"
                className="block w-full text-center bg-brand_yellow text-black px-6 py-3 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
                onClick={() => setMobileMenuOpen(false)}
              >
                Login
              </Link>
            )}
            <Link
              to="/cart"
              className="block w-full text-center bg-brand_blue text-white px-6 py-3 rounded-full font-nunito font-bold border-2 border-black shadow-hard"
              onClick={() => setMobileMenuOpen(false)}
            >
              Cart ({cartCount})
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;